import { useEffect, useState } from 'react'
import { supabase } from './supabase'
import { getFlag } from './featureFlags'

/**
 * React hook: subscribe to a single feature flag for a family.
 *
 * Returns `false` while loading, when familyId is null/undefined, or when the
 * row doesn't exist. Never throws — a network error or missing row degrades
 * silently to "not enabled" (off-by-default safety posture).
 *
 * Subscribes to Supabase Realtime for row-level UPDATEs on the matching row,
 * so a gradual rollout flip on the server side propagates to open tabs without
 * a page refresh. Also does an initial fetch on mount / familyId change so
 * server-side flag state is always the source of truth.
 *
 * Usage:
 *   const showNewOnboarding = useFeatureFlag(familyId, 'onboarding_v2')
 *   if (showNewOnboarding) return <OnboardingV2 />
 *
 * W4 — 2026-09-11. Prereq for I2 analytics gate + every W-phase risky rollout.
 */
export function useFeatureFlag(familyId: string | null | undefined, key: string): boolean {
  const [enabled, setEnabled] = useState<boolean>(false)

  useEffect(() => {
    let cancelled = false

    // Initial fetch — resolves the current state on mount / familyId change.
    if (familyId) {
      getFlag(familyId, key).then((value) => {
        if (!cancelled) setEnabled(value)
      })
    } else {
      setEnabled(false)
    }

    // Realtime subscription for flag flips while the tab is open. The channel
    // filter is on family_id + flag_key so we only wake on the row we care
    // about; UPDATE events give us the new `enabled` value directly.
    if (!familyId) return () => { cancelled = true }

    const channel = supabase
      .channel(`feature_flag:${familyId}:${key}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'feature_flags',
          filter: `family_id=eq.${familyId}`,
        },
        (payload) => {
          if (cancelled) return
          // Payload arrives for any row change in the family — filter to our key.
          const newRow = (payload.new as { flag_key?: string; enabled?: boolean } | null) ?? null
          const oldRow = (payload.old as { flag_key?: string } | null) ?? null
          const changedKey = newRow?.flag_key ?? oldRow?.flag_key
          if (changedKey !== key) return
          if (payload.eventType === 'DELETE') {
            setEnabled(false)
          } else if (newRow && typeof newRow.enabled === 'boolean') {
            setEnabled(newRow.enabled)
          }
        },
      )
      .subscribe()

    return () => {
      cancelled = true
      supabase.removeChannel(channel).catch(() => {
        // Non-fatal — channel cleanup best-effort
      })
    }
  }, [familyId, key])

  return enabled
}
